import { useState, useCallback, useRef } from 'react';
import type { PageInfo, PageState, SerializedToken, SessionState, LocationLink, MapOwner } from '@roll1/shared';

export interface UsePagesReturn {
  pages: PageInfo[];
  activePageId: string;
  viewingPageId: string;
  isPreview: boolean;
  navigationMode: 'free' | 'confirm' | 'gm-only';

  /**
   * Populate from v2 SessionState on join. Returns the data of the page the
   * canvas shows next: the GM's own view kept across a reconnect, else the
   * active page.
   */
  initFromState: (state: SessionState) => PageState | null;
  /** Increments on every initFromState (a join snapshot replaced the store). */
  hydrationSeq: number;
  /**
   * The same counter, written synchronously by initFromState: canvas hooks
   * loaded under an older value are older than the store and must not be
   * saved over it (a GM Studio map pane checks this on unmount and on echoes
   * that arrive before it has reloaded).
   */
  hydrationSeqRef: React.MutableRefObject<number>;
  /**
   * Id of the page whose data is currently loaded into the table's canvas
   * hooks (tokens / drawing / fog / notes / pins / walls) — '' when the table
   * does not hold them. Lives with the page store, above the StudioGate
   * table ⇄ Studio fork, so the table knows on its next mount whether the hooks
   * still hold its page. Written only by usePageHydration's loader and
   * released by the table on unmount. A GM Studio map pane has its own
   * (PageViewScope).
   */
  hydratedPageIdRef: React.MutableRefObject<string>;

  /** Read/write page data in the store */
  getPageData: (pageId: string) => PageState | null;
  setPageData: (pageId: string, data: PageState) => void;

  /** Server message handlers */
  handlePageList: (pages: PageInfo[], activePageId: string) => void;
  handlePageCreated: (page: PageInfo) => void;
  /** GM Studio map snapshots: a cloned page arrives with FULL state + the source
   * it was cloned from — store the authoritative state and group both pages. */
  handleSnapshotCreated: (page: PageInfo, pageState: PageState, fromPageId: string) => void;
  handlePageRenamed: (pageId: string, name: string) => void;
  /** P03: whose the map is (GM only) — the list and the cached page state carry it. */
  handlePageOwnerChanged: (pageId: string, owner: MapOwner) => void;
  /** P06 (F-6): a map was nested into another (null = taken out) — the list and the cached page state carry it. */
  handlePageParentChanged: (pageId: string, parentPageId: string | null) => void;
  /** A deleted viewed page sends the viewer back to the players' page. */
  handlePageDeleted: (pageId: string) => void;
  /**
   * The players' page changed. The viewer follows only if they were on the
   * players' page: a GM's own view (preview) changes by their own navigation,
   * never by an activation (GM Studio 5.1; replaces FR-008a's «always»).
   */
  handlePageActivated: (pageId: string, pageState: PageState) => void;
  handlePageReordered: (pageIds: string[]) => void;
  handleTokensTransferred: (
    sourcePageId: string,
    targetPageId: string,
    tokenIds: string[],
    tokens: SerializedToken[],
  ) => void;

  /** Location link handlers */
  handleLocationLinkCreated: (pageId: string, link: LocationLink) => void;
  handleLocationLinkUpdated: (pageId: string, link: LocationLink) => void;
  handleLocationLinkDeleted: (pageId: string, linkId: string) => void;
  handleMapChanged: (pageId: string, url: string | null) => void;
  handleNavigationModeChanged: (mode: 'free' | 'confirm' | 'gm-only') => void;

  /** Build breadcrumb path from root to given pageId */
  getBreadcrumb: (pageId: string) => PageInfo[];

  /** GM Preview */
  previewPage: (pageId: string) => void;
  returnToActive: () => void;

  /** Direct setter (used during page switch orchestration) */
  setViewingPageId: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * GJ-11: the map the GM looks at «by himself» (not the players') survives a reload of the tab: the choice is
 * kept per campaign in sessionStorage — after F5 the table opened on the players' map, whatever the GM was preparing.
 */
const viewKey = (campaignId: string): string => `roll1:own-view:${campaignId}`;
function readOwnView(campaignId: string | undefined): string {
  if (!campaignId || typeof sessionStorage === 'undefined') return '';
  try { return sessionStorage.getItem(viewKey(campaignId)) ?? ''; } catch { return ''; }
}
function writeOwnView(campaignId: string | undefined, pageId: string, activeId: string): void {
  if (!campaignId || typeof sessionStorage === 'undefined') return;
  try {
    if (pageId === '' || pageId === activeId) sessionStorage.removeItem(viewKey(campaignId));
    else sessionStorage.setItem(viewKey(campaignId), pageId);
  } catch { /* storage unavailable: the view just does not survive a reload */ }
}

export function usePages(campaignId?: string): UsePagesReturn {
  const campaignRef = useRef(campaignId);
  campaignRef.current = campaignId;
  const [pages, setPages] = useState<PageInfo[]>([]);
  const [activePageId, setActivePageIdState] = useState('');
  const [viewingPageId, setViewingPageIdState] = useState('');
  const [navigationMode, setNavigationMode] = useState<'free' | 'confirm' | 'gm-only'>('confirm');
  const store = useRef<Record<string, PageState>>({});
  const hydratedPageIdRef = useRef('');
  const [hydrationSeq, setHydrationSeq] = useState(0);
  const hydrationSeqRef = useRef(0);
  // Latest values, written together with the state: handlers that outlive a
  // render (a surface's unmount cleanup) and WS messages handled before the
  // next render must not decide on the page of an older render.
  const activeRef = useRef('');
  const viewingRef = useRef('');
  const setActivePageId = useCallback((pageId: string) => {
    activeRef.current = pageId;
    setActivePageIdState(pageId);
  }, []);
  const setViewingPageId = useCallback((next: React.SetStateAction<string>) => {
    const pageId = typeof next === 'function' ? next(viewingRef.current) : next;
    viewingRef.current = pageId;
    setViewingPageIdState(pageId);
    writeOwnView(campaignRef.current, pageId, activeRef.current);
  }, []);

  const isPreview = viewingPageId !== '' && viewingPageId !== activePageId;

  const getPageData = useCallback((pageId: string): PageState | null => {
    return store.current[pageId] ?? null;
  }, []);

  const setPageData = useCallback((pageId: string, data: PageState) => {
    store.current[pageId] = data;
  }, []);

  /* ---- initialise from v2 server state ---- */
  const initFromState = useCallback((state: SessionState): PageState | null => {
    // A reconnect keeps the GM's own view if that page still exists: a dropped
    // socket is not a navigation. A first join (nothing viewed yet) and a view
    // that was the players' page start on the active page.
    const ownView = viewingRef.current !== '' ? viewingRef.current : readOwnView(campaignRef.current);
    const keepOwnView =
      ownView !== '' && ownView !== activeRef.current && state.pages.some(p => p.id === ownView);
    const viewing = keepOwnView ? ownView : state.activePageId;
    store.current = {};
    // The hooks hold pre-snapshot data that must never be saved over the fresh
    // store; the caller loads the returned (viewed) page next.
    hydratedPageIdRef.current = '';
    for (const p of state.pages) store.current[p.id] = p;

    const infos: PageInfo[] = state.pages.map(p => ({
      id: p.id,
      name: p.name,
      sortOrder: p.sortOrder,
      isActive: p.id === state.activePageId,
      tokenCount: p.tokens.length,
      hasMap: p.mapUrl !== null,
      parentPageId: p.parentPageId,
      ...(p.owner !== undefined ? { owner: p.owner } : {}),
    }));
    setPages(infos);
    setActivePageId(state.activePageId);
    setViewingPageId(viewing);
    setNavigationMode(state.navigationMode ?? 'confirm');
    hydrationSeqRef.current += 1;
    setHydrationSeq(hydrationSeqRef.current);

    return store.current[viewing] ?? null;
  }, [setActivePageId, setViewingPageId]);

  /* ---- server message handlers ---- */

  const handlePageList = useCallback((newPages: PageInfo[], newActiveId: string) => {
    setPages(newPages);
    setActivePageId(newActiveId);
  }, [setActivePageId]);

  const handlePageCreated = useCallback((page: PageInfo) => {
    setPages(prev => [...prev, page]);
    if (!store.current[page.id]) {
      store.current[page.id] = {
        id: page.id,
        name: page.name,
        sortOrder: page.sortOrder,
        mapUrl: null,
        gridSize: 70,
        tokens: [],
        drawings: [],
        fog: { enabled: false, mode: 'manual', revealed: [], closings: [], explored: [] },
        parentPageId: page.parentPageId,
        locationLinks: [],
        notes: [],
        pins: [],
        annotations: [],
        triggers: [],
        textObjects: [],
        mapObjects: [],
        walls: [],
        globalIllumination: { level: 'bright' },
      };
    }
  }, []);

  const handleSnapshotCreated = useCallback(
    (page: PageInfo, pageState: PageState, fromPageId: string) => {
      // Full authoritative snapshot state (child ids match the server's).
      store.current[pageState.id] = pageState;
      const src = store.current[fromPageId];
      if (src) src.locationGroupId = pageState.locationGroupId ?? null;
      // Add the snapshot to the list AND back-fill the source's group so the
      // studio shows both as snapshots of one location.
      setPages(prev => [
        ...prev.map(p =>
          p.id === fromPageId ? { ...p, locationGroupId: page.locationGroupId ?? null } : p,
        ),
        page,
      ]);
    },
    [],
  );

  const handlePageRenamed = useCallback((pageId: string, name: string) => {
    setPages(prev => prev.map(p => (p.id === pageId ? { ...p, name } : p)));
    const d = store.current[pageId];
    if (d) d.name = name;
  }, []);

  const handlePageOwnerChanged = useCallback((pageId: string, owner: MapOwner) => {
    setPages(prev => prev.map(p => (p.id === pageId ? { ...p, owner } : p)));
    const d = store.current[pageId];
    if (d) d.owner = owner;
  }, []);

  const handlePageParentChanged = useCallback((pageId: string, parentPageId: string | null) => {
    setPages(prev => prev.map(p => (p.id === pageId ? { ...p, parentPageId } : p)));
    const d = store.current[pageId];
    if (d) d.parentPageId = parentPageId;
  }, []);

  const handlePageDeleted = useCallback((pageId: string) => {
    setPages(prev => prev.filter(p => p.id !== pageId));
    delete store.current[pageId];
    // Nothing is left to look at: back to the players' page (local only).
    if (viewingRef.current === pageId) setViewingPageId(activeRef.current);
  }, [setViewingPageId]);

  const handlePageActivated = useCallback(
    (pageId: string, pageState: PageState): void => {
      const previousActive = activeRef.current;
      setActivePageId(pageId);
      // A player holds only the map they were shown: it is not in their list yet, and the strip
      // «Игрок видит: …» takes the name from the list — so the shown map joins it here, with its name.
      setPages(prev => {
        const known = prev.some(p => p.id === pageId);
        const next = prev.map(p => ({ ...p, isActive: p.id === pageId, ...(p.id === pageId ? { name: pageState.name } : {}) }));
        if (known) return next;
        return [...next, {
          id: pageId,
          name: pageState.name,
          sortOrder: pageState.sortOrder,
          isActive: true,
          tokenCount: pageState.tokens?.length ?? 0,
          hasMap: !!pageState.mapUrl,
          parentPageId: pageState.parentPageId ?? null,
        }];
      });
      store.current[pageId] = pageState;
      // Whoever was on the players' page moves with them (players always are);
      // a GM's own view stays where the GM put it. A GM Studio map pane does
      // not use this view at all: its page is its own (PageViewScope).
      setViewingPageId(prev => (prev === '' || prev === previousActive ? pageId : prev));
    },
    [setActivePageId, setViewingPageId],
  );

  const handlePageReordered = useCallback((pageIds: string[]) => {
    setPages(prev => {
      const mapped = new Map(prev.map(p => [p.id, p]));
      return pageIds
        .map((id, i) => {
          const p = mapped.get(id);
          return p ? { ...p, sortOrder: i } : null;
        })
        .filter((p): p is PageInfo => p !== null);
    });
  }, []);

  const handleTokensTransferred = useCallback(
    (
      sourcePageId: string,
      targetPageId: string,
      tokenIds: string[],
      tokens: SerializedToken[],
    ) => {
      const src = store.current[sourcePageId];
      if (src) src.tokens = src.tokens.filter(t => !tokenIds.includes(t.id));
      const tgt = store.current[targetPageId];
      if (tgt) tgt.tokens.push(...tokens);
      setPages(prev =>
        prev.map(p => {
          if (p.id === sourcePageId && src) return { ...p, tokenCount: src.tokens.length };
          if (p.id === targetPageId && tgt) return { ...p, tokenCount: tgt.tokens.length };
          return p;
        }),
      );
    },
    [],
  );

  /* ---- GM Preview ---- */

  const previewPage = useCallback((pageId: string) => {
    setViewingPageId(pageId);
  }, [setViewingPageId]);

  // The players' page NOW — not the one of the render that created this
  // callback (Studio calls it from an unmount cleanup captured at mount).
  const returnToActive = useCallback(() => {
    setViewingPageId(activeRef.current);
  }, [setViewingPageId]);

  /* ---- Location link handlers ---- */

  const handleLocationLinkCreated = useCallback((pageId: string, link: LocationLink) => {
    const pageData = store.current[pageId];
    if (pageData) {
      pageData.locationLinks = [...pageData.locationLinks, link];
    }
    // Update parentPageId on the target page info if not set
    setPages(prev => prev.map(p => {
      if (p.id === link.targetPageId && p.parentPageId === null) {
        const targetData = store.current[p.id];
        if (targetData) targetData.parentPageId = pageId;
        return { ...p, parentPageId: pageId };
      }
      return p;
    }));
  }, []);

  const handleLocationLinkUpdated = useCallback((pageId: string, link: LocationLink) => {
    const pageData = store.current[pageId];
    if (pageData) {
      pageData.locationLinks = pageData.locationLinks.map(l => l.id === link.id ? link : l);
    }
  }, []);

  const handleLocationLinkDeleted = useCallback((pageId: string, linkId: string) => {
    const pageData = store.current[pageId];
    if (pageData) {
      pageData.locationLinks = pageData.locationLinks.filter(l => l.id !== linkId);
    }
  }, []);

  const handleNavigationModeChanged = useCallback((mode: 'free' | 'confirm' | 'gm-only') => {
    setNavigationMode(mode);
  }, []);

  /* ---- Breadcrumb ---- */

  /**
   * Build breadcrumb path from root to the given pageId.
   * Traverses parentPageId chain, stopping at depth 5 to prevent infinite cycles.
   */
  const getBreadcrumb = useCallback((pageId: string): PageInfo[] => {
    const MAX_DEPTH = 5;
    const path: PageInfo[] = [];
    const visited = new Set<string>();
    let currentId: string | null = pageId;

    while (currentId !== null && path.length < MAX_DEPTH) {
      if (visited.has(currentId)) break; // cycle detection
      visited.add(currentId);
      const info = pages.find(p => p.id === currentId);
      if (!info) break;
      path.unshift(info);
      currentId = info.parentPageId;
    }

    return path;
  }, [pages]);

  return {
    pages,
    activePageId,
    viewingPageId,
    isPreview,
    navigationMode,
    initFromState,
    hydrationSeq,
    hydrationSeqRef,
    hydratedPageIdRef,
    getPageData,
    setPageData,
    handlePageList,
    handlePageCreated,
    handleSnapshotCreated,
    handlePageRenamed,
    handlePageOwnerChanged,
    handlePageParentChanged,
    handlePageDeleted,
    handlePageActivated,
    handlePageReordered,
    handleTokensTransferred,
    handleLocationLinkCreated,
    handleLocationLinkUpdated,
    handleMapChanged: useCallback((pageId: string, url: string | null) => {
      // Update the mutable page store
      const pd = store.current[pageId];
      // '' (map:set clearing the image) means no image, as on the server.
      const mapUrl = url === null || url === '' ? null : url;
      if (pd) pd.mapUrl = mapUrl;
      // Update React state so toolbar re-renders
      setPages(prev => prev.map(p => p.id === pageId ? { ...p, hasMap: mapUrl !== null } : p));
    }, []),
    handleLocationLinkDeleted,
    handleNavigationModeChanged,
    getBreadcrumb,
    previewPage,
    returnToActive,
    setViewingPageId,
  };
}
